import React from 'react';
import { useQuery } from "@apollo/client";
import { CircularProgress, Typography, useTheme } from "@mui/joy";
import { AssetBreadcrumbPath } from "../../graphql/queries/assetBreadcrumbPath";
import { SystemBreadcrumbPath } from "../../graphql/queries/systemBreadcrumbPath";
import { InstalledBaseNodeAttributeName } from "../../graphql/queries/installedBaseNodeAttributeName";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { BreadcrumbComponentProps } from "use-react-router-breadcrumbs";

/**
 * Dynamic breadcrumb for Asset - renders the current node title or product name.
 * Parent path nodes are expanded into separate breadcrumb items at the route level.
 */
export const AssetNameCrumb = ({ match }: BreadcrumbComponentProps<string>) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const assetLocationId = match.params.assetLocationId;

    const { data, loading } = useQuery(AssetBreadcrumbPath, {
        variables: { id: assetLocationId },
        skip: !assetLocationId,
    });

    const assetNode = _.get(data, 'assetLocation.installedBaseNode');
    const assetProductName = _.get(data, 'assetLocation.asset.product.name', t('Unknown'));

    if (loading) {
        return <CircularProgress size="sm" />;
    }

    // Show the current installed base node title, or fallback to product name
    const title = assetNode?.title || assetProductName;

    return (
        <Typography
            level="body-sm"
            sx={{
                color: theme.palette.primary[500],
                fontWeight: "bold",
            }}
        >
            {title}
        </Typography>
    );
};

/**
 * Dynamic breadcrumb for System - renders the current installed base node title.
 * Parent path nodes are expanded into separate breadcrumb items at the route level.
 */
export const SystemNameCrumb = ({ match }: BreadcrumbComponentProps<string>) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const systemId = match.params.systemId;

    const { data, loading } = useQuery(SystemBreadcrumbPath, {
        variables: { id: systemId },
        skip: !systemId,
    });

    const title = _.get(data, 'system.installedBaseNode.title', t('Unknown'));

    if (loading) {
        return <CircularProgress size="sm" />;
    }

    return (
        <Typography
            level="body-sm"
            sx={{
                color: theme.palette.primary[500],
                fontWeight: "bold",
            }}
        >
            {title}
        </Typography>
    );
};

/**
 * Dynamic breadcrumb for Attribute - fetches preferredName
 */
export const AttributeNameCrumb = ({ match }: BreadcrumbComponentProps<string>) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const attributeId = match.params.attributeId;

    const { data, loading } = useQuery(InstalledBaseNodeAttributeName, {
        variables: { id: attributeId },
        skip: !attributeId,
    });

    const attributeName = _.get(data, 'installedBaseNodePropertyDefinition.preferredName', t('Unknown'));

    if (loading) {
        return <CircularProgress size="sm" />;
    }

    return (
        <Typography
            level="body-sm"
            sx={{
                color: theme.palette.primary[500],
                fontWeight: "bold",
            }}
        >
            {attributeName}
        </Typography>
    );
};
